
import { Component, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import { Recipe } from "@/models/Recipes/Recipe";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vPreLoader from "@/components/vPreLoader.vue";
import vModal from "@/components/vModal.vue";
import {downloadURIWithStatus, mapNutritions} from "@/utils/Helpers";
import { desanitizeString } from "@/utils/Helpers";
import { LocalizationState } from "@/store/modules/LocalizationModule";
import vRecipeListing from "@/components/vRecipeListing.vue";
import vRecipeDetails from "@/components/vRecipeDetails.vue";
import vAuditLogModal from "@/components/vAuditLogModal.vue";
import vCopyRecipeModal from "@/components/vCopyRecipeModal.vue";
import vBreakdownReportModal from "@/components/vBreakdownReportModal.vue";
import VAminoAcidScoreReport from "@/components/reports/vAminoAcidScoreReport.vue";
import vSingleNutrientReportModal from "@/components/reports/vSingleNutrientReportModal.vue";
import { ItemCompact } from "@/models/ItemCompact";
import vProteinQualityReport from "@/components/reports/vProteinQualityReport.vue";
import { resource as recipeResource } from "@/utils/API/RecipesRepository";
import vRecipeDeleteConfirmationModal from "@/components/vRecipeDeleteConfirmationModal.vue";
import {ErrorResponse} from "@/utils/API/ResultTypes/ErrorResponse";
import vMultiColumnReportModal from "@/components/vMultiColumnReportModal.vue";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import {AccountState} from "@/store/modules/AccountModule";
import ListRequest, { OrderType } from "@/models/ListRequest";
import { buildRecipeListingFilters } from "@/utils/recipeListingSearch";
import {
  buildRecipeCategoryPrintDocument,
  RecipeCategoryPrintSection,
} from "@/components/vRecipeCategoryPrintout.vue";
import { RecipeCategory } from "@/models/Recipes/RecipeCategory";
import vGenerateReportModal from "@/components/vGenerateReportModal.vue";
import { REPORT_NOTIFICATION_SHOW } from "@/store/ActionTypes";
import {
  BulkReportType,
  GenerateReportCategoryOption,
  GenerateReportOption,
  ReportCategoryScopeMode,
} from "@/models/Reports/GenerateReport";

interface RecipesState {
  recipe: Recipe;
  selectedRecipe: ItemCompact;
}

@Component({
  components: {
    VUpsellModal,
    VAminoAcidScoreReport,
    vBreadcrumbs,
    vPreLoader,
    vModal,
    vRecipeListing,
    vRecipeDetails,
    vAuditLogModal,
    vCopyRecipeModal,
    vBreakdownReportModal,
    vSingleNutrientReportModal,
    vProteinQualityReport,
    vRecipeDeleteConfirmationModal,
    vMultiColumnReportModal,
    vGenerateReportModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
  methods: {
    desanitizeString
  }
})
class Recipes extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;

  recipesState: RecipesState = {
    recipe: new Recipe(),
    selectedRecipe: {
      id: 0,
      name: "",
      createdOn: null,
      updatedOn: null,
      createdUserName: "",
      updatedUserName: ""
    },
  };

  recipeForCopy: Recipe = new Recipe();
  checkedRecipesForDelete: number[] = [];
  funcHandler :any;

  // Modal states
  showDeleteRecipeModal = false;
  showCopyRecipeModal = false;
  showBreakdownReportModal = false;
  showProteinQualityReportModal = false;
  showAminoAcidScoreReportModal = false;
  showSingleNutrientReportModal = false;
  showMultiColumnReportModal = false;
  showGenerateReportModal = false;

  /**
   * Indicates whether the "Download Bulk Recipe Report" button is disabled.
   *
   * When set to `true`, the button is disabled and cannot be interacted with by the user.
   * When set to `false`, the button is enabled and available for user interaction.
   */
  isGeneratingReport = false;
  selectedBulkReportType: BulkReportType = BulkReportType.Summary;
  selectedBulkReportCountry = "0";
  selectedCategoryIds: number[] = [];
  categoryScopeMode: ReportCategoryScopeMode = "all";
  availablePrintCategories: RecipeCategory[] = [];
  categoryRecipeCounts: Record<number, number> = {};

  showTrialUpsellModal: boolean = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  isRecipeDetailsLoading = false;
  lastRecipeDetailsRequestId = 0;
  trialUpsellTransitionTimeoutId: number | null = null;

  openTrialUpsellModal(message: string = this.defaultTrialUpsellMessage) {
    this.trialUpsellMessage = message;
    this.showTrialUpsellModal = true;
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  async transitionGenerateReportModalToTrialUpsell(message: string = this.defaultTrialUpsellMessage) {
    this.showGenerateReportModal = false;

    if (this.trialUpsellTransitionTimeoutId !== null) {
      window.clearTimeout(this.trialUpsellTransitionTimeoutId);
      this.trialUpsellTransitionTimeoutId = null;
    }

    await this.$nextTick();

    this.trialUpsellTransitionTimeoutId = window.setTimeout(() => {
      this.openTrialUpsellModal(message);
      this.trialUpsellTransitionTimeoutId = null;
    }, 180);
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  async handleAddRecipeClick() {
    await this.$router.push({ name: "recipesAdd" });
  }

  get reportTypeOptions(): GenerateReportOption[] {
    return [
      {
        value: BulkReportType.Summary,
        name: this.translations.get("Summary"),
        description: this.translations.get("reports_summary_option_description"),
      },
      {
        value: BulkReportType.Breakdown,
        name: this.translations.get("breakdown"),
        description: this.translations.get("reports_breakdown_option_description"),
      },
      {
        value: BulkReportType.CategoryCoreNutrient,
        name: this.translations.get("reports_category_core_nutrient_option"),
        description: this.translations.get("reports_category_core_nutrient_option_description"),
      },
      {
        value: BulkReportType.PrintCategory,
        name: this.translations.get("reports_print_category_option"),
        description: this.translations.get("reports_print_category_option_description"),
      },
    ];
  }

  get countryOptions(): GenerateReportOption[] {
    return [
      { value: "0", name: this.translations.get("canada") },
      { value: "1", name: this.translations.get("country_us") },
      { value: "2", name: this.translations.get("country_uk") },
    ];
  }

  get printCategoryOptions(): GenerateReportCategoryOption[] {
    return this.availablePrintCategories.map((category) => ({
      value: category.id,
      name: category.name,
    }));
  }

  get selectedCategoryName() {
    if (this.categoryScopeMode === "all") {
      return "All Categories";
    }

    return this.availablePrintCategories
      .filter((category) => this.selectedCategoryIds.includes(category.id))
      .map((category) => category.name)
      .join(", ");
  }

  get isReportActionDisabled() {
    return false;
  }

  get isGenerateReportSubmitDisabled() {
    return (
      this.categoryScopeMode === "selected"
      && this.selectedCategoryIds.length === 0
    ) || this.selectedRecipeCount === 0;
  }

  get selectedRecipeCountSummary() {
    if (this.availablePrintCategories.length === 0) {
      return this.translations.get("reports_loading_recipe_count");
    }

    const hasAllCategoryCounts = this.availablePrintCategories.every((category) =>
      typeof this.categoryRecipeCounts[category.id] === "number"
    );

    if (!hasAllCategoryCounts) {
      return this.translations.get("reports_loading_recipe_count");
    }

    const recipeCount = this.selectedRecipeCount;

    return recipeCount === 1
      ? this.translations.get("reports_recipe_count_single")
      : this.translations.get("reports_recipe_count_plural").replace("{count}", String(recipeCount));
  }

  get selectedRecipeCount() {
    return this.categoryScopeMode === "all"
      ? this.availablePrintCategories.reduce((total, category) => total + (this.categoryRecipeCounts[category.id] || 0), 0)
      : this.selectedCategoryIds.reduce((total, categoryId) => total + (this.categoryRecipeCounts[categoryId] || 0), 0);
  }

  get shouldShowCountrySelector() {
    return this.selectedBulkReportType === BulkReportType.Summary
      || this.selectedBulkReportType === BulkReportType.Breakdown;
  }

  changeBulkReportType(value: string) {
    this.selectedBulkReportType = value as BulkReportType;
  }

  changeBulkReportCountry(value: string) {
    this.selectedBulkReportCountry = value;
  }

  changePrintCategory(value: number[]) {
    this.selectedCategoryIds = value;
  }

  setCategoryScopeMode(mode: ReportCategoryScopeMode) {
    this.categoryScopeMode = mode;

    if (mode === "all") {
      this.selectedCategoryIds = [];
    }
  }

  print() {
    window.print();
  }

  openReport(id: number) {
    if (this.accountState.userDetails.isTrial && id !== 5) {
      this.openTrialUpsellModal();
      return;
    }

    switch (id) {
    case 0:
      this.openRecipeReportModal();
      break;
    case 1:
      this.openProteinQualityReportModal();
      break;
    case 2:
      this.openAminoAcidScoreReportModal();
      break;
    case 3:
      this.openSingleNutrientReportModal();
      break;
    case 4:
      this.openMultiColumnReportModal();
      break;
    case 5:
      this.openGenerateReportModal(BulkReportType.CategoryCoreNutrient);
      break;
    }
  }

  openGenerateReportModal(defaultReportType: BulkReportType = BulkReportType.Summary) {
    this.selectedBulkReportType = defaultReportType;
    this.selectedBulkReportCountry = "0";
    this.categoryScopeMode = "all";
    this.selectedCategoryIds = [];
    if (this.availablePrintCategories.length === 0) {
      void this.loadPrintCategories();
    }
    this.showGenerateReportModal = true;
  }

  closeGenerateReportModal() {
    this.showGenerateReportModal = false;
  }

  // Open modal and view the recipe report
  openRecipeReportModal() {
    this.showBreakdownReportModal = true;
  }

  openProteinQualityReportModal() {
    this.showProteinQualityReportModal = true;
  }

  hideProteinQualityReportModal() {
    this.showProteinQualityReportModal = false;
  }

  openAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = true;
  }

  hideAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = false;
  }

  openSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = true;
  }

  hideSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = false;
  }

  openMultiColumnReportModal(){
    this.showMultiColumnReportModal = true;
  }

  hideMultiColumnReportModal(){
    this.showMultiColumnReportModal = false;
  }

  openDeleteModal(recipeId: number[], handler: (recipeIds: number[]) => void): void {
    this.checkedRecipesForDelete = recipeId;
    this.showDeleteRecipeModal = true;
    this.funcHandler = handler;
  }

  openCopyModal() {
    this.showCopyRecipeModal = true;
    RecipesRepository.getRecipeDetails(this.recipesState.recipe.id).then(
      (response) => {
        this.recipeForCopy = response.data;
        this.recipeForCopy.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
      }
    );
  }

  onAuditLogClick(recipe: ItemCompact) {
    this.auditLogModal.isOpen = true;
    this.recipesState.selectedRecipe = { ...recipe };
  }

  cancelAuditModal(){
    this.auditLogModal.isOpen = false;
  }

  /**
   * Click handler for showing selected recipe
   * */
  async chooseRecipe(recipeId: number) {
    this.isRecipeDetailsLoading = true;
    const requestId = ++this.lastRecipeDetailsRequestId;

    try {
      const response = await RecipesRepository.getRecipeDetails(recipeId, true);

      if (requestId !== this.lastRecipeDetailsRequestId) {
        return;
      }

      this.recipesState.recipe = { ...response.data };
      this.recipesState.recipe.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
    }
    catch (error: any) {
      if (requestId !== this.lastRecipeDetailsRequestId) {
        return;
      }

      if (error.response) {
        this.$snotify.error(error.response.data?.errorMessages?.[0]?.userVisibleMessage || error.response.data?.message);
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
    finally {
      if (requestId === this.lastRecipeDetailsRequestId) {
        this.isRecipeDetailsLoading = false;
      }
    }
  }

  saveCopiedRecipe(newRecipeName : string) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_loading"),
      () =>
        new Promise((success, reject) => {
          RecipesRepository.copyRecipe(this.recipeForCopy.id, newRecipeName)
            .then((response) => {
              this.showCopyRecipeModal = false;
              this.$router.push({
                name: "recipesEdit",
                params: { id: `${response.data.id}` },
              });
              success({
                body: this.translations.get("rec_copy_recipe_success"),
              });
            })
            .catch((error: ErrorResponse) => {
              this.showCopyRecipeModal = false;
              let message;

              if (error.response) {
                message = error.response.data.message;
              }
              else {
                message = "Unknown error while copying item.";
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }

  /**
   * Method which requests delete recipes to API and shows toastr message
   */
  removeRecipes() {
    this.funcHandler(this.checkedRecipesForDelete);
    this.showDeleteRecipeModal = false;
  }

  get summaryReportUrl() {
    return this.buildRecipesExportUrl("0");
  }

  get breakdownReportUrl() {
    return this.buildRecipesExportUrl("1");
  }

  get categoryCoreNutrientReportUrl() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/processes/reports/CategoryMenuReportDownload?countryId=${this.selectedBulkReportCountry}${this.selectedCategoryQueryString}`;
  }

  buildRecipesExportUrl(reportType: string) {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes${recipeResource}/reports/ExportRecipes?RecipesReportType=${reportType}&country=${this.selectedBulkReportCountry}${this.selectedCategoryQueryString}`;
  }

  get selectedCategoryQueryString() {
    if (this.categoryScopeMode === "all") {
      return "";
    }

    const categoryIds = this.selectedCategoryIds.join(",");
    return categoryIds ? `&recipeCategoryIds=${categoryIds}` : "";
  }

  getSelectedCountryLabel() {
    switch (this.selectedBulkReportCountry) {
    case "0":
      return "Canada";
    case "1":
      return "USA";
    case "2":
      return "UK";
    default:
      return "Canada";
    }
  }

  async handleGenerateReport() {
    if (this.isGenerateReportSubmitDisabled) {
      return;
    }

    if (this.accountState.userDetails.isTrial) {
      await this.transitionGenerateReportModalToTrialUpsell();
      return;
    }

    this.showGenerateReportModal = false;

    try {
      switch (this.selectedBulkReportType) {
      case BulkReportType.Summary:
        this.isGeneratingReport = true;
        if (await downloadURIWithStatus(
          this.summaryReportUrl,
          `Recipes Summary (${this.getSelectedCountryLabel()})`
        )) {
          this.$store.dispatch(REPORT_NOTIFICATION_SHOW, {
            message: "Your recipes summary download is now available.",
          });
        }
        break;
      case BulkReportType.Breakdown:
        this.isGeneratingReport = true;
        if (await downloadURIWithStatus(
          this.breakdownReportUrl,
          `Recipes Breakdown (${this.getSelectedCountryLabel()})`
        )) {
          this.$store.dispatch(REPORT_NOTIFICATION_SHOW, {
            message: "Your recipes breakdown download is now available.",
          });
        }
        break;
      case BulkReportType.CategoryCoreNutrient:
        this.isGeneratingReport = true;
        if (await downloadURIWithStatus(
          this.categoryCoreNutrientReportUrl,
          `Category Menu Report (${this.getSelectedCountryLabel()}).xlsx`
        )) {
          this.$store.dispatch(REPORT_NOTIFICATION_SHOW, {
            message: "Your category menu report download is now available.",
          });
        }
        break;
      case BulkReportType.PrintCategory:
        await this.printSelectedCategoryRecipes();
        break;
      }
    }
    finally {
      this.isGeneratingReport = false;
    }
  }

  async printSelectedCategoryRecipes() {
    if (this.categoryScopeMode === "selected" && this.selectedCategoryIds.length === 0) {
      this.$snotify.error("Select at least one category before generating this report.");
      return;
    }

    const categoryIdsToPrint = this.categoryScopeMode === "all"
      ? this.availablePrintCategories.map((category) => category.id)
      : this.selectedCategoryIds;

    const responses = await Promise.all(
      categoryIdsToPrint.map((categoryId) => {
        const listRequest = new ListRequest({
          page: 1,
          pageSize: 5000,
          filters: buildRecipeListingFilters(categoryId, { name: "", value: "" }),
          orders: [{ field: "Name", orderType: OrderType.Asc }],
        });

        return RecipesRepository.listReducedRecipes(listRequest, true);
      })
    );

    const sections: RecipeCategoryPrintSection[] = categoryIdsToPrint.map((categoryId, index) => {
      const matchingCategory = this.availablePrintCategories.find((category) => category.id === categoryId);
      const recipes = Array.isArray(responses[index]?.data?.result) ? responses[index].data.result : [];

      return {
        categoryName: matchingCategory?.name || "Category",
        recipes,
      };
    });

    const printWindow = window.open("", "_blank", "width=900,height=700");

    if (!printWindow) {
      this.$snotify.error("Unable to open the print window.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(
      buildRecipeCategoryPrintDocument("Recipe Categories", sections)
    );
    printWindow.document.close();

    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 200);
  }

  async loadPrintCategories() {
    try {
      const response = await RecipesRepository.listRecipeCategories();
      const categories = Array.isArray(response.data?.result)
        ? response.data.result
        : Array.isArray(response.data)
          ? response.data
          : [];

      this.availablePrintCategories = categories
      .slice()
      .sort((a: RecipeCategory, b: RecipeCategory) => a.name.localeCompare(b.name));

      const categoryCountEntries = await Promise.all(
        this.availablePrintCategories.map(async (category) => {
          const listRequest = new ListRequest({
            page: 1,
            pageSize: 1,
            filters: buildRecipeListingFilters(category.id, { name: "", value: "" }),
            orders: [{ field: "Name", orderType: OrderType.Asc }],
          });

          try {
            const countResponse = await RecipesRepository.listReducedRecipes(listRequest, true);
            return [category.id, Number(countResponse.data?.totalCount || 0)] as const;
          }
          catch {
            return [category.id, 0] as const;
          }
        })
      );

      this.categoryRecipeCounts = categoryCountEntries.reduce((acc, [categoryId, totalCount]) => {
        acc[categoryId] = totalCount;
        return acc;
      }, {} as Record<number, number>);

      if (this.categoryScopeMode === "selected" && this.selectedCategoryIds.length > 0) {
        this.selectedCategoryIds = this.selectedCategoryIds.filter((categoryId) =>
          this.availablePrintCategories.some((category) => category.id === categoryId)
        );
      }
    }
    catch (error: any) {
      this.availablePrintCategories = [];
      this.categoryRecipeCounts = {};
      if (error?.response) {
        this.$snotify.error(
          error.response.data?.errorMessages?.[0]?.userVisibleMessage || error.response.data?.message
        );
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
  }

  auditLogModal = {
    isOpen: false
  };

  get translations() {
    return this.localizationState.translations;
  }

  get isTrial() {
    return this.$store.getters.getUserDetails.isTrial;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "recipes", "ingredients"],
      })
      .then(() => {
        document.title =
          this.translations.get("rec_title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }
}
export default Recipes;
